/**
 * Loading Components
 * Coláiste Lurgan Management System - Reusable loading states
 */

import { useTranslation } from 'react-i18next';

/**
 * Standard spinner component
 */
export function LoadingSpinner({ size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8', 
    lg: 'h-12 w-12'
  };

  return (
    <div className={`animate-spin rounded-full border-b-2 border-primary ${sizeClasses[size]} ${className}`} />
  );
}

/**
 * Full page loading screen with optional message
 */
export function LoadingScreen({ message, showTip = false }) {
  const { t } = useTranslation();
  
  const defaultMessage = message || t('common.loading', 'Ag luchtú... / Loading...');
  const tip = t('loading.tip', 'Molann sé go bhfanfaidh tú foighneach... / Please be patient...');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md mx-auto p-6">
        <LoadingSpinner size="lg" className="mx-auto mb-6" />
        <p className="text-lg font-medium mb-2">{defaultMessage}</p>
        {showTip && (
          <p className="text-sm text-muted-foreground">{tip}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Inline loading state for components
 */
export function InlineLoading({ message, size = 'sm' }) {
  const { t } = useTranslation();
  const defaultMessage = message || t('common.loading', 'Loading...');

  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <LoadingSpinner size={size} />
      <span className="text-sm">{defaultMessage}</span>
    </div>
  );
}

/**
 * Loading overlay for forms/cards
 */
export function LoadingOverlay({ message, children }) {
  const { t } = useTranslation();
  const defaultMessage = message || t('common.processing', 'Ag próiseáil... / Processing...');

  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{defaultMessage}</p>
        </div>
      </div>
    </div>
  );
}